import { useCallback } from 'react';

import { useSelector } from 'react-redux';

import { StreamTypes } from 'store/player/actions.types';
import { EpgProgram, RBProgram } from 'store/railsBuilder/reducer.types';
import { RecordingItem } from 'store/recordings/reducer.types';
import {
    selectAppSessionId,
    selectVideoSessionId,
} from 'store/session/selectors';

import { VodEditorial } from 'services/data-manager/types';
import { ScheduleRecordingsUavParams, UavEvents } from 'services/uav/types';
import {
    MOBILE_PLAYBACK_CONTENT_SOURCE,
    getPlayBackType,
    getTechnicalId,
} from 'services/uav/utils';

import { getDefaultMetadata, isRecordingItem } from 'utils/recordings';

import useGetBaseUavParams from './useGetBaseUavParams';

type GetScheduleRecordingsParams = Omit<
    ScheduleRecordingsUavParams['metadata'],
    'appSessionId' | 'contentType' | 'contentSource' | 'appName' | 'appVersion'
>;

function useGetScheduleRecordingsParams(
    playbackType: StreamTypes | undefined,
    item: RBProgram | EpgProgram | VodEditorial | RecordingItem | undefined,
) {
    const appSessionId = useSelector(selectAppSessionId);
    const genericVideoSessionId = useSelector(selectVideoSessionId);

    const getBaseUavParams = useGetBaseUavParams();

    const getScheduleRecordingsParams = useCallback(
        (
            metrics: GetScheduleRecordingsParams,
        ): ScheduleRecordingsUavParams | undefined => {
            const baseUavParams = getBaseUavParams(
                UavEvents.SCHEDULE_RECORDING,
            );

            if (baseUavParams && item && appSessionId) {
                const editorialID = isRecordingItem(item)
                    ? getDefaultMetadata(item)?.editorial?.id
                    : item.id;
                const programmeId = isRecordingItem(item)
                    ? item.contentId
                    : item.id;

                return {
                    ...baseUavParams,
                    metadata: {
                        ...metrics,
                        appSessionId,
                        contentSource: MOBILE_PLAYBACK_CONTENT_SOURCE,
                        contentType: getPlayBackType(playbackType),
                        playbackSessionId: genericVideoSessionId,
                        editorialID,
                        technicalId:
                            getTechnicalId(item as VodEditorial | RBProgram) ??
                            item.id,
                        programmeId,
                        deeplinkId: programmeId,
                        ...baseUavParams.metadata,
                    },
                };
            }

            return undefined;
        },
        [
            getBaseUavParams,
            appSessionId,
            playbackType,
            genericVideoSessionId,
            item,
        ],
    );

    return {
        getScheduleRecordingsParams,
    };
}

export default useGetScheduleRecordingsParams;
